import React, { useEffect, useState } from 'react';

// First-time nickname prompt. Opens automatically after sign-in if the
// user hasn't set a nickname yet. Pre-fills with the first token of their
// auth-provider name so they can accept it as-is. "Skip" leaves the
// nickname empty — the dashboard falls through to first-name then email.
// Avatar dropdown also routes here later when the user wants to change.

const MAX_LEN = 40;

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '1rem',
  fontFamily: theme.typography?.fontFamily || 'serif',
  boxSizing: 'border-box',
});

export const NicknamePromptModal = ({
  theme,
  currentNickname,
  suggestedNickname,
  isFirstTime = true,
  onSave,
  onSkip,
  onClose,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setValue((currentNickname || suggestedNickname || '').slice(0, MAX_LEN));
  }, [currentNickname, suggestedNickname]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!onSave) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(value.trim());
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) { onClose && onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await onSkip();
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Set your nickname"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 250,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onSubmit={handleSave}
        style={{
          width: '100%', maxWidth: 460,
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
          fontFamily: t.fontFamily,
        }}
      >
        <h2 style={{
          margin: '0 0 6px',
          fontFamily: t.headingFamily,
          fontSize: '1.3rem',
          color: c.accent,
        }}>{isFirstTime ? 'What should we call you?' : 'Change your nickname'}</h2>
        <p style={{
          margin: '0 0 18px',
          color: c.textMuted,
          fontSize: '0.88rem',
          lineHeight: 1.5,
        }}>
          {isFirstTime
            ? 'This is the name your DMs and party will see on the dashboard. You can change it any time.'
            : 'Updated immediately on your dashboard and on every campaign you are part of.'}
        </p>

        <input
          type="text"
          value={value}
          maxLength={MAX_LEN}
          placeholder="e.g. Wren"
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          style={fieldStyle(theme)}
        />
        <div style={{
          marginTop: 4,
          fontFamily: t.fontFamily,
          fontSize: '0.72rem',
          color: c.textMuted,
        }}>{value.length} / {MAX_LEN}</div>

        {error && (
          <div style={{
            marginTop: 12,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.84rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          {isFirstTime && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={busy}
              style={{
                padding: '9px 14px',
                background: 'transparent',
                color: c.textMuted,
                border: `1px solid ${c.borderLight}`,
                borderRadius: 4,
                cursor: busy ? 'wait' : 'pointer',
                fontFamily: t.fontFamily,
                fontSize: '0.86rem',
              }}
            >Skip</button>
          )}
          {!isFirstTime && onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                padding: '9px 14px',
                background: 'transparent',
                color: c.textSecondary,
                border: `1px solid ${c.borderLight}`,
                borderRadius: 4,
                cursor: busy ? 'wait' : 'pointer',
                fontFamily: t.fontFamily,
                fontSize: '0.86rem',
              }}
            >Cancel</button>
          )}
          <button
            type="submit"
            disabled={busy || !value.trim()}
            style={{
              padding: '9px 18px',
              background: c.accent,
              color: c.textOnAccent || '#000',
              border: 'none',
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              opacity: (busy || !value.trim()) ? 0.7 : 1,
            }}
          >{busy ? 'Saving…' : (isFirstTime ? 'Save' : 'Save nickname')}</button>
        </div>
      </form>
    </div>
  );
};
